import { useState, useEffect, useCallback } from 'react';
import {
  Scale,
  Loader2,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  Shield,
  Users,
  FileText,
  Download,
  TrendingUp,
  Clock,
  X,
} from 'lucide-react';

// Types
interface DailyCount {
  date: string;
  count: number;
}

interface HighRiskUser {
  userId: number;
  userName: string;
  email: string;
  reportCount: number;
  banCount: number;
  lastReportReason: string;
  lastReportDate: string;
}

interface ComplianceDashboard {
  totalFlaggedUsers: number;
  totalBannedUsers: number;
  totalPendingReports: number;
  totalResolvedReports: number;
  reportsTrend: DailyCount[];
  bansTrend: DailyCount[];
  highRiskUsers: HighRiskUser[];
  averageResolutionTimeHours: number;
  reportsResolvedLast7Days: number;
  reportsResolvedLast30Days: number;
}

// API helper
async function fetchApi<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `HTTP ${response.status}`);
  }

  return response.json();
}

export function CompliancePage() {
  // State
  const [dashboard, setDashboard] = useState<ComplianceDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Load dashboard
  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchApi<ComplianceDashboard>('/api/v1/admin/compliance/dashboard');
      setDashboard(data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      // Use mock data for development
      setDashboard(getMockDashboard());
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Export data
  const exportData = async (type: 'reports' | 'users') => {
    setIsExporting(true);
    try {
      const response = await fetch(`/api/v1/admin/compliance/export?type=${type}`, {
        credentials: 'include',
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance_${type}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  // Load on mount
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Mock data for development
  function getMockDashboard(): ComplianceDashboard {
    const reportsTrend: DailyCount[] = [];
    const bansTrend: DailyCount[] = [];
    const today = new Date();

    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;

      reportsTrend.push({
        date: dateStr,
        count: Math.floor(Math.random() * 10) + 1,
      });
      bansTrend.push({
        date: dateStr,
        count: Math.floor(Math.random() * 3),
      });
    }

    return {
      totalFlaggedUsers: 156,
      totalBannedUsers: 23,
      totalPendingReports: 42,
      totalResolvedReports: 328,
      reportsTrend,
      bansTrend,
      highRiskUsers: [
        { userId: 1, userName: 'Bad Actor 1', email: 'bad1@example.com', reportCount: 8, banCount: 2, lastReportReason: 'HARASSMENT', lastReportDate: '2025-01-15T10:30:00Z' },
        { userId: 2, userName: 'Spammer User', email: 'spam@example.com', reportCount: 6, banCount: 1, lastReportReason: 'SPAM', lastReportDate: '2025-01-14T14:20:00Z' },
        { userId: 3, userName: 'Offensive User', email: 'offensive@example.com', reportCount: 5, banCount: 1, lastReportReason: 'OFFENSIVE_CONTENT', lastReportDate: '2025-01-13T09:15:00Z' },
        { userId: 4, userName: 'Fake Profile', email: 'fake@example.com', reportCount: 4, banCount: 0, lastReportReason: 'IMPERSONATION', lastReportDate: '2025-01-12T16:45:00Z' },
        { userId: 5, userName: 'Scammer User', email: 'scam@example.com', reportCount: 3, banCount: 1, lastReportReason: 'SCAM', lastReportDate: '2025-01-11T11:00:00Z' },
      ],
      averageResolutionTimeHours: 18.5,
      reportsResolvedLast7Days: 45,
      reportsResolvedLast30Days: 156,
    };
  }

  // Simple sparkline chart
  const SparklineChart = ({ data, color }: { data: DailyCount[]; color: string }) => {
    const maxCount = Math.max(...data.map(d => d.count), 1);
    const height = 40;
    const width = 200;

    const points = data.map((d, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - (d.count / maxCount) * height;
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg width={width} height={height} className="overflow-visible">
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  // Format time ago
  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Scale className="h-7 w-7 text-cyan-400" />
            Compliance Dashboard
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Track violations, reports, and compliance metrics
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => exportData('reports')}
            disabled={isExporting}
            className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 rounded-lg transition-colors"
          >
            <Download className="h-4 w-4" />
            Export Reports
          </button>
          <button
            onClick={loadDashboard}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 rounded-lg transition-colors"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 p-4 mb-4 bg-red-900/20 border border-red-800/50 rounded-lg text-red-400">
          <AlertCircle className="h-5 w-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
        </div>
      ) : dashboard ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="p-6 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-amber-400">{dashboard.totalPendingReports}</div>
                  <div className="text-sm text-slate-400 mt-1">Pending Reports</div>
                </div>
                <AlertTriangle className="h-10 w-10 text-amber-400/50" />
              </div>
            </div>

            <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-red-400">{dashboard.totalBannedUsers}</div>
                  <div className="text-sm text-slate-400 mt-1">Banned Users</div>
                </div>
                <Shield className="h-10 w-10 text-red-400/50" />
              </div>
            </div>

            <div className="p-6 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-blue-400">{dashboard.totalFlaggedUsers}</div>
                  <div className="text-sm text-slate-400 mt-1">Flagged Users</div>
                </div>
                <Users className="h-10 w-10 text-blue-400/50" />
              </div>
            </div>

            <div className="p-6 bg-green-500/10 border border-green-500/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-green-400">{dashboard.totalResolvedReports}</div>
                  <div className="text-sm text-slate-400 mt-1">Resolved Reports</div>
                </div>
                <FileText className="h-10 w-10 text-green-400/50" />
              </div>
            </div>
          </div>

          {/* Trend Charts */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Reports Trend */}
            <div className="p-6 bg-slate-800/50 border border-slate-700 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-medium text-white">Reports Trend</h3>
                  <p className="text-xs text-slate-500">Last 30 days</p>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp className="h-4 w-4 text-amber-400" />
                  <span className="text-amber-400">
                    {dashboard.reportsTrend.slice(-7).reduce((a, b) => a + b.count, 0)} this week
                  </span>
                </div>
              </div>
              <SparklineChart data={dashboard.reportsTrend} color="#f59e0b" />
            </div>

            {/* Resolution Metrics */}
            <div className="p-6 bg-slate-800/50 border border-slate-700 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-medium text-white">Resolution Metrics</h3>
                  <p className="text-xs text-slate-500">Performance tracking</p>
                </div>
                <Clock className="h-5 w-5 text-cyan-400" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-2xl font-bold text-cyan-400">
                    {dashboard.averageResolutionTimeHours.toFixed(1)}h
                  </div>
                  <div className="text-xs text-slate-500">Avg Resolution Time</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-400">
                    {dashboard.reportsResolvedLast7Days}
                  </div>
                  <div className="text-xs text-slate-500">Resolved (7 days)</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-400">
                    {dashboard.reportsResolvedLast30Days}
                  </div>
                  <div className="text-xs text-slate-500">Resolved (30 days)</div>
                </div>
              </div>
            </div>
          </div>

          {/* High-Risk Users */}
          <div className="p-6 bg-slate-800/50 border border-slate-700 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium text-white">High-Risk Users</h3>
                <p className="text-xs text-slate-500">Users with 3+ reports</p>
              </div>
              <button
                onClick={() => exportData('users')}
                disabled={isExporting}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded transition-colors"
              >
                <Download className="h-3 w-3" />
                Export
              </button>
            </div>

            {dashboard.highRiskUsers.length === 0 ? (
              <div className="py-8 text-center text-slate-500">
                No high-risk users found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-500 text-xs uppercase tracking-wider">
                      <th className="text-left pb-3">User</th>
                      <th className="text-left pb-3">Email</th>
                      <th className="text-center pb-3">Reports</th>
                      <th className="text-center pb-3">Bans</th>
                      <th className="text-left pb-3">Last Report</th>
                      <th className="text-left pb-3">When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {dashboard.highRiskUsers.map((user) => (
                      <tr key={user.userId} className="hover:bg-slate-700/30">
                        <td className="py-3">
                          <div className="font-medium text-white">{user.userName}</div>
                          <div className="text-xs text-slate-500">ID: {user.userId}</div>
                        </td>
                        <td className="py-3 text-slate-300">{user.email}</td>
                        <td className="py-3 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            user.reportCount >= 5
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {user.reportCount}
                          </span>
                        </td>
                        <td className="py-3 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            user.banCount > 0
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-slate-500/20 text-slate-400'
                          }`}>
                            {user.banCount}
                          </span>
                        </td>
                        <td className="py-3">
                          <span className="px-2 py-0.5 bg-slate-700 rounded text-xs text-slate-300">
                            {user.lastReportReason}
                          </span>
                        </td>
                        <td className="py-3 text-slate-400 text-xs">
                          {formatTimeAgo(user.lastReportDate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-slate-500">
          <Scale className="h-12 w-12 mb-3 opacity-50" />
          <p>No data available</p>
        </div>
      )}
    </div>
  );
}
